import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type {
  Transaction,
  Category,
  Budget,
  ClassificationCorrection,
} from '../types';
import {
  saveData,
  loadData,
  clearAllData as clearIndexedDB,
} from '../lib/persistence';
import type { PersistenceKey } from '../lib/persistence';
import { getDefaultCategories } from '../services/categoryService';

interface AppState {
  transactions: Transaction[];
  categories: Category[];
  budgets: Budget[];
  corrections: ClassificationCorrection[];

  isLoading: boolean;
  isInitialized: boolean;
}

interface AppActions {
  addTransactions: (transactions: Transaction[]) => void;
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;

  addCategory: (category: Category) => void;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  deleteCategory: (id: string) => void;

  setBudget: (budget: Budget) => void;
  deleteBudget: (id: string) => void;

  addCorrection: (correction: ClassificationCorrection) => void;

  loadFromStorage: () => Promise<void>;
  clearAllData: () => Promise<void>;
}

export type AppStore = AppState & AppActions;

const initialState: AppState = {
  transactions: [],
  categories: [],
  budgets: [],
  corrections: [],
  isLoading: true,
  isInitialized: false,
};

export const useAppStore = create<AppStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      ...initialState,

      addTransactions: (transactions) =>
        set(
          (state) => {
            const existingIds = new Set(state.transactions.map((t) => t.id));
            const newTransactions = transactions.filter(
              (t) => !existingIds.has(t.id)
            );
            return {
              transactions: [...state.transactions, ...newTransactions],
            };
          },
          false,
          'addTransactions'
        ),

      updateTransaction: (id, updates) =>
        set(
          (state) => ({
            transactions: state.transactions.map((t) =>
              t.id === id ? { ...t, ...updates } : t
            ),
          }),
          false,
          'updateTransaction'
        ),

      deleteTransaction: (id) =>
        set(
          (state) => ({
            transactions: state.transactions.filter((t) => t.id !== id),
          }),
          false,
          'deleteTransaction'
        ),

      addCategory: (category) =>
        set(
          (state) => ({
            categories: [...state.categories, category],
          }),
          false,
          'addCategory'
        ),

      updateCategory: (id, updates) =>
        set(
          (state) => ({
            categories: state.categories.map((c) =>
              c.id === id ? { ...c, ...updates } : c
            ),
          }),
          false,
          'updateCategory'
        ),

      deleteCategory: (id) =>
        set(
          (state) => ({
            categories: state.categories.filter((c) => c.id !== id),
          }),
          false,
          'deleteCategory'
        ),

      setBudget: (budget) =>
        set(
          (state) => {
            const existingIndex = state.budgets.findIndex(
              (b) => b.id === budget.id
            );
            if (existingIndex >= 0) {
              const newBudgets = [...state.budgets];
              newBudgets[existingIndex] = budget;
              return { budgets: newBudgets };
            }
            return { budgets: [...state.budgets, budget] };
          },
          false,
          'setBudget'
        ),

      deleteBudget: (id) =>
        set(
          (state) => ({
            budgets: state.budgets.filter((b) => b.id !== id),
          }),
          false,
          'deleteBudget'
        ),

      addCorrection: (correction) =>
        set(
          (state) => ({
            corrections: [...state.corrections, correction],
          }),
          false,
          'addCorrection'
        ),

      loadFromStorage: async () => {
        set({ isLoading: true }, false, 'loadFromStorage/start');

        try {
          const [
            transactionsData,
            categoriesData,
            budgetsData,
            correctionsData,
          ] = await Promise.all([
            loadData<Transaction[]>('transactions'),
            loadData<Category[]>('categories'),
            loadData<Budget[]>('budgets'),
            loadData<ClassificationCorrection[]>('corrections'),
          ]);

          // Initialize default categories if none exist
          const loadedCategories = categoriesData?.data ?? [];
          const categories =
            loadedCategories.length > 0
              ? loadedCategories
              : getDefaultCategories();

          set(
            {
              transactions: transactionsData?.data ?? [],
              categories,
              budgets: budgetsData?.data ?? [],
              corrections: correctionsData?.data ?? [],
              isLoading: false,
              isInitialized: true,
            },
            false,
            'loadFromStorage/success'
          );
        } catch (error) {
          console.error('Failed to load data from storage:', error);
          set(
            { isLoading: false, isInitialized: true },
            false,
            'loadFromStorage/error'
          );
        }
      },

      clearAllData: async () => {
        try {
          await clearIndexedDB();
          set(
            {
              transactions: [],
              categories: [],
              budgets: [],
              corrections: [],
            },
            false,
            'clearAllData'
          );
        } catch (error) {
          console.error('Failed to clear data:', error);
          throw error;
        }
      },
    })),
    { name: 'AppStore' }
  )
);

let debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};
const DEBOUNCE_MS = 1000;

function createDebouncedSave(key: PersistenceKey) {
  return <T>(data: T) => {
    if (debounceTimers[key]) {
      clearTimeout(debounceTimers[key]);
    }
    debounceTimers[key] = setTimeout(() => {
      saveData(key, data);
    }, DEBOUNCE_MS);
  };
}

const debouncedSaveTransactions = createDebouncedSave('transactions');
const debouncedSaveCategories = createDebouncedSave('categories');
const debouncedSaveBudgets = createDebouncedSave('budgets');
const debouncedSaveCorrections = createDebouncedSave('corrections');

useAppStore.subscribe(
  (state) => state.transactions,
  (transactions) => {
    if (useAppStore.getState().isInitialized) {
      debouncedSaveTransactions(transactions);
    }
  }
);

useAppStore.subscribe(
  (state) => state.categories,
  (categories) => {
    if (useAppStore.getState().isInitialized) {
      debouncedSaveCategories(categories);
    }
  }
);

useAppStore.subscribe(
  (state) => state.budgets,
  (budgets) => {
    if (useAppStore.getState().isInitialized) {
      debouncedSaveBudgets(budgets);
    }
  }
);

useAppStore.subscribe(
  (state) => state.corrections,
  (corrections) => {
    if (useAppStore.getState().isInitialized) {
      debouncedSaveCorrections(corrections);
    }
  }
);

export function resetDebounceTimers() {
  Object.values(debounceTimers).forEach(clearTimeout);
  debounceTimers = {};
}

export const useIsLoading = () => useAppStore((state) => state.isLoading);
export const useIsInitialized = () =>
  useAppStore((state) => state.isInitialized);
